import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  Tag,
  DollarSign,
  Palette,
  FileText,
  Check,
  X,
  Upload,
  Clock,
  Building2,
  RefreshCw,
  ShoppingBag,
  Download,
  CheckSquare,
  Square,
  Layers,
  Ruler,
  Droplet,
  Copy,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import { ColourSwatch, useSupplierColours, useAllColours } from "@/components/ColourSwatch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Size system presets
const SIZE_TYPE_OPTIONS = [
  { value: "standard_sizes", label: "Standard Apparel (XS-5XL)" },
  { value: "numeric_sizes", label: "Numeric (4-20)" },
  { value: "custom_sizes", label: "Custom Sizes" },
  { value: "single_size", label: "One Size / No Sizes" },
];

const SIZE_PRESETS = {
  standard_sizes: ["2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"],
  numeric_sizes: ["4", "6", "8", "10", "12", "14", "16", "18", "20"],
  kids_sizes: ["000", "00", "0", "1", "2", "3", "4", "5", "6", "7", "8", "10", "12", "14"],
  hat_sizes: ["54cm", "56cm", "58cm", "60cm", "62cm"],
  single_size: ["One Size"],
};

const PRODUCT_TYPE_OPTIONS = [
  "T-Shirt",
  "Polo",
  "Hoodie",
  "Jumper",
  "Jacket",
  "Pants",
  "Shorts",
  "Cap",
  "Beanie",
  "Bag",
  "Apron",
  "Hi-Vis",
  "Workwear",
  "Kids",
  "Other",
];

const EMPTY_PRODUCT = {
  brand: "",
  style_code: "",
  product_name: "",
  supplier: "",
  default_cost: 0,
  retail_price: 0,
  available_colours: [],
  size_type: "standard_sizes",
  sizes: [],
  product_type: "",
  notes: "",
  active: true,
};

export default function ProductLibrary() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("catalogue");
  const [suppliers, setSuppliers] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [productTypeFilter, setProductTypeFilter] = useState("all");
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [coloursInput, setColoursInput] = useState("");
  const [sizesInput, setSizesInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Bulk edit state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [bulkSizesInput, setBulkSizesInput] = useState("");
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  
  // Size preset state
  const [isSizePresetOpen, setIsSizePresetOpen] = useState(false);
  const [presetProductType, setPresetProductType] = useState("");
  const [presetSizeType, setPresetSizeType] = useState("standard_sizes");
  const [presetSizes, setPresetSizes] = useState("");
  
  // Supplier colours state
  const [supplierColours, setSupplierColours] = useState([]);
  const [selectedColourIds, setSelectedColourIds] = useState([]);
  const [colourInputMode, setColourInputMode] = useState("library"); // "library" or "manual"
  
  // Get all colours for lookup
  const { colourMap, getColour } = useAllColours();

  useEffect(() => {
    loadProducts();
    loadSuppliers();
  }, [showInactive, supplierFilter, productTypeFilter]);

  useEffect(() => {
    if (activeTab === "recent") {
      loadRecentProducts();
    }
  }, [activeTab]);

  // Restore an in-progress product if the user left to create supplier colours
  useEffect(() => {
    const raw = localStorage.getItem("product_draft");
    if (!raw) return;
    localStorage.removeItem("product_draft");
    try {
      const d = JSON.parse(raw);
      setFormData(d.formData || EMPTY_PRODUCT);
      setColoursInput(d.coloursInput || "");
      setSizesInput(d.sizesInput || "");
      setSelectedColourIds(d.selectedColourIds || []);
      setColourInputMode(d.colourInputMode || "library");
      setEditingProduct(d.editingProductId ? { id: d.editingProductId } : null);
      if (d.formData?.supplier) loadSupplierColours(d.formData.supplier);
      setIsModalOpen(true);
      toast.success("Restored your in-progress product");
    } catch (e) {
      // Ignore malformed draft
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProducts = async (searchQuery = "") => {
    setIsLoading(true);
    try {
      const params = { 
        search: searchQuery, 
        active_only: !showInactive, 
        limit: 100 
      };
      if (supplierFilter && supplierFilter !== "all") {
        params.supplier = supplierFilter;
      }
      if (productTypeFilter && productTypeFilter !== "all") {
        params.product_type = productTypeFilter;
      }
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to load products", error);
      toast.error("Failed to load products");
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentProducts = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/products`, { 
        params: { recently_used: true, limit: 50 } 
      });
      setRecentProducts(res.data.products || []);
    } catch (error) {
      console.error("Failed to load recent products", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/products/suppliers`);
      setSuppliers(res.data.suppliers || []);
    } catch (error) {
      console.error("Failed to load suppliers", error);
    }
  };

  // Load supplier colours when supplier changes in form
  const loadSupplierColours = async (supplier) => {
    if (!supplier) {
      setSupplierColours([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/supplier-colours`, {
        params: { supplier, active_only: true }
      });
      setSupplierColours(res.data.colours || []);
    } catch (error) {
      console.error("Failed to load supplier colours", error);
      setSupplierColours([]);
    }
  };

  // Toggle colour selection
  const toggleColourSelection = (colourId) => {
    setSelectedColourIds(prev => 
      prev.includes(colourId) 
        ? prev.filter(id => id !== colourId)
        : [...prev, colourId]
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadProducts(search);
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData(EMPTY_PRODUCT);
    setColoursInput("");
    setSizesInput("");
    setSelectedColourIds([]);
    setSupplierColours([]);
    setColourInputMode("library");
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      brand: product.brand || "",
      style_code: product.style_code || "",
      product_name: product.product_name || "",
      supplier: product.supplier || "",
      default_cost: product.default_cost || 0,
      retail_price: product.retail_price || 0,
      available_colours: product.available_colours || [],
      size_type: product.size_type || "standard_sizes",
      sizes: product.sizes || [],
      product_type: product.product_type || "",
      notes: product.notes || "",
      active: product.active !== false,
    });
    setColoursInput((product.available_colours || []).join(", "));
    setSizesInput((product.sizes || []).join(", "));
    // Set colour IDs if present, otherwise use available_colours
    setSelectedColourIds(product.colour_ids || []);
    // Determine mode based on whether product has colour_ids
    setColourInputMode(product.colour_ids?.length > 0 ? "library" : "manual");
    // Load supplier colours if we have a supplier
    if (product.supplier) {
      loadSupplierColours(product.supplier);
    } else {
      setSupplierColours([]);
    }
    setIsModalOpen(true);
  };

  // Duplicate an existing product into a new (unsaved) product
  const openDuplicateModal = (product) => {
    setEditingProduct(null); // null => handleSave creates a NEW product
    setFormData({
      brand: product.brand || "",
      style_code: product.style_code || "",
      product_name: `${product.product_name || product.brand || "Product"} (Copy)`,
      supplier: product.supplier || "",
      default_cost: product.default_cost || 0,
      retail_price: product.retail_price || 0,
      available_colours: product.available_colours || [],
      size_type: product.size_type || "standard_sizes",
      sizes: product.sizes || [],
      product_type: product.product_type || "",
      notes: product.notes || "",
      active: product.active !== false,
    });
    setColoursInput((product.available_colours || []).join(", "));
    setSizesInput((product.sizes || []).join(", "));
    setSelectedColourIds(product.colour_ids || []);
    setColourInputMode(product.colour_ids?.length > 0 ? "library" : "manual");
    if (product.supplier) {
      loadSupplierColours(product.supplier);
    } else {
      setSupplierColours([]);
    }
    setIsModalOpen(true);
    toast.info("Editing a copy — review and click Create to save");
  };

  // ---- In-progress product draft (so creating a supplier colour mid-add doesn't lose data) ----
  const stashProductDraft = () => {
    try {
      localStorage.setItem(
        "product_draft",
        JSON.stringify({
          formData,
          coloursInput,
          sizesInput,
          selectedColourIds,
          colourInputMode,
          editingProductId: editingProduct?.id || null,
        })
      );
    } catch (e) {
      // localStorage may be unavailable; non-critical
    }
  };

  const handleSave = async () => {
    if (!formData.brand && !formData.product_name) {
      toast.error("Brand or Product Name is required");
      return;
    }
    
    setIsSaving(true);
    try {
      // Parse colours from input (for manual mode)
      const colours = coloursInput
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c);
      
      // Parse sizes from input
      const sizes = sizesInput
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s);
      
      // Build available_colours from library selection or manual input
      let availableColours = colours;
      if (colourInputMode === "library" && selectedColourIds.length > 0) {
        // Get colour names from selected IDs
        const selectedColourNames = supplierColours
          .filter(c => selectedColourIds.includes(c.id))
          .map(c => c.name);
        availableColours = selectedColourNames;
      }
      
      const payload = { 
        ...formData, 
        available_colours: availableColours,
        colour_ids: colourInputMode === "library" ? selectedColourIds : [],
        sizes: sizes,
      };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success("Product created");
      }
      
      setIsModalOpen(false);
      loadProducts(search);
      loadSuppliers();
    } catch (error) {
      console.error("Failed to save product", error);
      toast.error("Failed to save product");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted");
      loadProducts(search);
    } catch (error) {
      console.error("Failed to delete product", error);
      toast.error("Failed to delete product");
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/products/import-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} products`);
      loadProducts(search);
      loadSuppliers();
    } catch (error) {
      console.error("Failed to import CSV", error);
      toast.error(error.response?.data?.detail || "Failed to import CSV");
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/products/export-csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Products exported to CSV");
    } catch (error) {
      console.error("Failed to export CSV", error);
      toast.error("Failed to export CSV");
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Select all visible products
  const selectAllProducts = () => {
    const visibleIds = products.map(p => p.id);
    if (selectedProducts.length === visibleIds.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(visibleIds);
    }
  };

  // Bulk update products
  const handleBulkUpdate = async () => {
    if (selectedProducts.length === 0) {
      toast.error("No products selected");
      return;
    }
    
    setIsBulkSaving(true);
    try {
      let updates = {};
      
      if (bulkEditField === "product_type") {
        updates = { product_type: bulkEditValue };
      } else if (bulkEditField === "size_type") {
        updates = { size_type: bulkEditValue };
      } else if (bulkEditField === "sizes") {
        const sizes = bulkSizesInput.split(",").map(s => s.trim()).filter(s => s);
        updates = { sizes };
      } else if (bulkEditField === "supplier") {
        updates = { supplier: bulkEditValue };
      }
      
      await axios.post(`${API}/products/bulk-update`, {
        product_ids: selectedProducts,
        updates
      });
      
      toast.success(`Updated ${selectedProducts.length} products`);
      setIsBulkEditOpen(false);
      setSelectedProducts([]);
      setBulkEditField("");
      setBulkEditValue("");
      setBulkSizesInput("");
      loadProducts(search);
    } catch (error) {
      console.error("Bulk update failed", error);
      toast.error(error.response?.data?.detail || "Bulk update failed");
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Apply size preset to product type
  const handleApplySizePreset = async () => {
    if (!presetProductType && selectedProducts.length === 0) {
      toast.error("Select products or a product type");
      return;
    }
    
    const sizes = presetSizes.split(",").map(s => s.trim()).filter(s => s);
    if (sizes.length === 0) {
      toast.error("Enter at least one size");
      return;
    }
    
    setIsBulkSaving(true);
    try {
      const payload = {
        size_type: presetSizeType,
        sizes
      };
      
      if (selectedProducts.length > 0) {
        payload.product_ids = selectedProducts;
      } else {
        payload.product_type = presetProductType;
      }
      
      const res = await axios.post(`${API}/products/apply-size-preset`, payload);
      
      toast.success(`Applied sizes to ${res.data.modified} products`);
      setIsSizePresetOpen(false);
      setSelectedProducts([]);
      setPresetProductType("");
      setPresetSizes("");
      loadProducts(search);
    } catch (error) {
      console.error("Apply preset failed", error);
      toast.error(error.response?.data?.detail || "Apply preset failed");
    } finally {
      setIsBulkSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    try {
      return new Date(dateStr).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const ProductCard = ({ product, showUsage = false }) => (
    <div className={`bg-white rounded-lg p-4 border border-workspace-border shadow-sm transition-all hover:shadow-md ${
      selectedProducts.includes(product.id) ? 'ring-2 ring-orange-500 ring-offset-2' : ''
    }`}>
      <div className="flex items-start justify-between gap-3">
        {/* Selection checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleProductSelection(product.id); }}
          className="mt-0.5 flex-shrink-0 text-workspace-text-muted hover:text-orange-500"
        >
          {selectedProducts.includes(product.id) ? (
            <CheckSquare className="w-5 h-5 text-orange-500" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {product.style_code && (
              <span className="px-2 py-0.5 bg-workspace-bg rounded font-mono text-xs text-workspace-text-secondary">
                {product.style_code}
              </span>
            )}
            <span className="font-medium text-sm text-workspace-text-primary truncate">
              {product.brand} {product.product_name}
            </span>
            {!product.active && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                Inactive
              </span>
            )}
          </div>
          
          {product.supplier && (
            <div className="text-xs text-workspace-text-muted flex items-center gap-1 mb-1">
              <Building2 className="w-3 h-3" />
              {product.supplier}
            </div>
          )}
          
          {/* Product Type & Size info */}
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            {product.product_type && (
              <Badge variant="outline" className="text-[10px] py-0">
                {product.product_type}
              </Badge>
            )}
            {product.sizes && product.sizes.length > 0 && (
              <Badge variant="secondary" className="text-[10px] py-0 font-mono">
                <Ruler className="w-2.5 h-2.5 mr-0.5" />
                {product.sizes.length} sizes
              </Badge>
            )}
          </div>
          
          {/* Pricing - Admin sees both, Staff sees retail only */}
          <div className="flex items-center gap-3 mt-1">
            {isAdmin && (
              <div className="text-sm">
                <span className="text-workspace-text-muted">Wholesale:</span>{" "}
                <span className="font-semibold text-green-600">
                  ${parseFloat(product.default_cost || 0).toFixed(2)}
                </span>
              </div>
            )}
            <div className="text-sm">
              <span className="text-workspace-text-muted">Retail:</span>{" "}
              <span className="font-semibold text-orange-600">
                ${parseFloat(product.retail_price || 0).toFixed(2)}
              </span>
            </div>
          </div>
          
          {product.available_colours?.length > 0 && (
            <div className="flex gap-1 mt-2 flex-wrap">
              {product.available_colours.slice(0, 5).map((c) => {
                const colourData = getColour(c, product.supplier);
                return (
                  <span key={c} className="flex items-center gap-1 px-2 py-0.5 bg-workspace-bg text-xs text-workspace-text-secondary rounded">
                    <ColourSwatch hex={colourData?.hex} size="xs" />
                    {c}
                  </span>
                );
              })}
              {product.available_colours.length > 5 && (
                <span className="px-2 py-0.5 bg-workspace-bg text-xs text-workspace-text-secondary rounded">
                  +{product.available_colours.length - 5}
                </span>
              )}
            </div>
          )}
          
          {showUsage && (
            <div className="text-xs text-workspace-text-muted mt-2 flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                Used {product.usage_count || 0}x
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(product.last_used)}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex gap-1">
          <button
            onClick={() => openEditModal(product)}
            data-testid={`edit-product-${product.id}`}
            className="p-2 rounded hover:bg-workspace-bg text-workspace-text-muted hover:text-orange-500 transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => openDuplicateModal(product)}
            data-testid={`duplicate-product-${product.id}`}
            className="p-2 rounded hover:bg-workspace-bg text-workspace-text-muted hover:text-blue-500 transition-colors"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(product.id)}
            data-testid={`delete-product-${product.id}`}
            className="p-2 rounded hover:bg-red-50 text-workspace-text-muted hover:text-red-500 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const allProductTypes = Array.from(
    new Set([
      ...PRODUCT_TYPE_OPTIONS,
      ...products.map((p) => p.product_type).filter(Boolean),
    ])
  );

  return (
    <WorkspaceLayout
      title="Products"
      actions={
        <div className="flex items-center gap-2">
          {/* Hidden file input for CSV import */}
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleImportCSV}
            className="hidden"
            data-testid="csv-import-input"
          />
          
          {/* Export CSV Button */}
          <button
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-workspace-border text-sm font-medium hover:bg-workspace-hover transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          
          {/* Import CSV Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            data-testid="import-csv-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-workspace-border text-sm font-medium hover:bg-workspace-hover transition-colors disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {isImporting ? "Importing..." : "Import CSV"}
          </button>
          
          {/* Add Product Button */}
          <button 
            onClick={openAddModal} 
            data-testid="add-product-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
          
          {/* Refresh Button */}
          <button
            onClick={() => loadProducts(search)}
            disabled={isLoading}
            className="p-2 rounded-md bg-white border border-workspace-border hover:bg-workspace-hover transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      }
    >
      {/* Search and Filters Bar */}
      <div className="bg-white border-b border-workspace-border px-6 py-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 bg-workspace-bg rounded-md">
            <button
              onClick={() => setActiveTab("catalogue")}
              data-testid="tab-catalogue"
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                activeTab === "catalogue"
                  ? "bg-white shadow-sm text-workspace-text-primary" 
                  : "text-workspace-text-secondary hover:text-workspace-text-primary"
              }`}
            >
              <Package className="w-4 h-4 inline mr-1.5" />
              Catalogue
            </button>
            <button
              onClick={() => setActiveTab("recent")}
              data-testid="tab-recent"
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                activeTab === "recent"
                  ? "bg-white shadow-sm text-workspace-text-primary" 
                  : "text-workspace-text-secondary hover:text-workspace-text-primary"
              }`}
            >
              <Clock className="w-4 h-4 inline mr-1.5" />
              Recently Used
            </button>
          </div>
          
          <div className="h-6 w-px bg-workspace-border" />
          
          {/* Search */}
          {activeTab === "catalogue" && (
            <>
              <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-workspace-text-muted" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="product-search-input"
                    className="w-full pl-10 pr-4 py-2 bg-workspace-bg rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 border border-workspace-border"
                  />
                </div>
              </form>
              
              {/* Filters */}
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="w-40 bg-white rounded-md border-workspace-border" data-testid="supplier-filter">
                  <Building2 className="w-4 h-4 mr-2 text-workspace-text-muted" />
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent className="rounded-md bg-white border border-workspace-border">
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={productTypeFilter} onValueChange={setProductTypeFilter}>
                <SelectTrigger className="w-36 bg-white rounded-md border-workspace-border" data-testid="product-type-filter">
                  <Tag className="w-4 h-4 mr-2 text-workspace-text-muted" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="rounded-md bg-white border border-workspace-border">
                  <SelectItem value="all">All Types</SelectItem>
                  {allProductTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Show Inactive Toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  id="show-inactive"
                />
                <Label htmlFor="show-inactive" className="text-sm text-workspace-text-secondary">
                  Show inactive
                </Label>
              </div>
            </>
          )}
          
          {/* Product Count */}
          <div className="ml-auto text-sm text-workspace-text-secondary">
            {total} products
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {/* Import Result */}
        {importResult && (
          <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-700 text-sm">
                  Import Complete: {importResult.imported} products imported
                </p>
                {importResult.skipped > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    {importResult.skipped} rows skipped (missing data)
                  </p>
                )}
                {importResult.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 cursor-pointer">
                      {importResult.errors.length} errors
                    </summary>
                    <ul className="text-xs text-red-600 mt-1 list-disc pl-4">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <button
                onClick={() => setImportResult(null)}
                className="p-2 rounded hover:bg-green-100 transition-colors"
              >
                <X className="w-4 h-4 text-green-700" />
              </button>
            </div>
          </div>
        )}

        {/* Catalogue Tab Content */}
        {activeTab === "catalogue" && (
          <div className="space-y-4">
            {/* Products Grid */}
            {isLoading ? (
              <div className="text-center py-12 text-workspace-text-muted">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                <p className="text-sm">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 text-workspace-text-muted">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium mb-1 text-workspace-text-primary">No products found</h3>
                <p className="text-sm">Add products manually or import from CSV</p>
              </div>
            ) : (
              <>
                {/* Bulk Actions Bar */}
                <div className="flex items-center justify-between py-3 px-4 bg-workspace-bg rounded-md border border-workspace-border">
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={selectAllProducts}
                      className="flex items-center gap-2 text-sm text-workspace-text-secondary hover:text-workspace-text-primary"
                    >
                      {selectedProducts.length === products.length && products.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-orange-500" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      Select All
                    </button>
                    {selectedProducts.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {selectedProducts.length} selected
                      </Badge>
                    )}
                  </div>
                  
                  {selectedProducts.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsBulkEditOpen(true)}
                        className="text-xs"
                      >
                        <Layers className="w-3 h-3 mr-1" />
                        Bulk Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsSizePresetOpen(true)}
                        className="text-xs"
                      >
                        <Ruler className="w-3 h-3 mr-1" />
                        Apply Sizes
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedProducts([])}
                        className="text-xs text-workspace-text-muted"
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Recently Used Tab Content */}
        {activeTab === "recent" && (
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12 text-workspace-text-muted">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                <p className="text-sm">Loading recent products...</p>
              </div>
            ) : recentProducts.length === 0 ? (
              <div className="text-center py-12 text-workspace-text-muted">
                <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium mb-1 text-workspace-text-primary">No recent products</h3>
                <p className="text-sm">Products you use in quotes will appear here</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {recentProducts.map((product) => (
                  <ProductCard key={product.id} product={product} showUsage />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Product Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md bg-white rounded-lg max-h-[90vh] flex flex-col p-0 gap-0">
          {/* Fixed Header */}
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-workspace-border shrink-0">
            <DialogTitle className="text-lg font-semibold text-workspace-text-primary">
              {editingProduct ? "Edit Product" : "Add Product"}
            </DialogTitle>
            <DialogDescription className="text-xs text-workspace-text-muted">
              {editingProduct
                ? "Update product details. Changes won't affect existing quotes."
                : "Add a new product to your library."}
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label htmlFor="brand" className="text-xs font-medium text-workspace-text-secondary">Brand</label>
                <input
                  id="brand"
                  type="text"
                  value={formData.brand}
                  onChange={(e) =>
                    setFormData({ ...formData, brand: e.target.value })
                  }
                  placeholder="AS Colour"
                  className="w-full h-9 px-3 bg-workspace-bg rounded-md border border-workspace-border text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  data-testid="product-brand-input"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="style_code" className="text-xs font-medium text-workspace-text-secondary">Style Code</label>
                <input
                  id="style_code"
                  type="text"
                  value={formData.style_code}
                  onChange={(e) =>
                    setFormData({ ...formData, style_code: e.target.value })
                  }
                  placeholder="5001"
                  className="w-full h-9 px-3 bg-workspace-bg rounded-md border border-workspace-border text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  data-testid="product-style-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="product_name" className="text-xs font-medium text-workspace-text-secondary">Product Name</label>
              <input
                id="product_name"
                type="text"
                value={formData.product_name}
                onChange={(e) =>
                  setFormData({ ...formData, product_name: e.target.value })
                }
                placeholder="Staple Tee"
                className="w-full h-9 px-3 bg-workspace-bg rounded-md border border-workspace-border text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                data-testid="product-name-input"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="supplier" className="text-xs font-medium text-workspace-text-secondary">Supplier</label>
              <input
                id="supplier"
                type="text"
                value={formData.supplier}
                onChange={(e) => {
                  const newSupplier = e.target.value;
                  setFormData({ ...formData, supplier: newSupplier });
                  // Load colours for the new supplier when it matches known suppliers
                  if (newSupplier && suppliers.includes(newSupplier)) {
                    loadSupplierColours(newSupplier);
                  }
                }}
                onBlur={(e) => {
                  // Also load colours on blur in case user typed a new supplier
                  if (e.target.value) {
                    loadSupplierColours(e.target.value);
                  }
                }}
                placeholder="AS Colour"
                list="supplier-list"
                className="w-full h-9 px-3 bg-workspace-bg rounded-md border border-workspace-border text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                data-testid="product-supplier-input"
              />
              <datalist id="supplier-list">
                {suppliers.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>

            {/* Pricing Section - Two columns for wholesale and retail */}
            <div className="grid grid-cols-2 gap-3">
              {isAdmin && (
                <div className="space-y-1">
                  <label htmlFor="default_cost" className="flex items-center gap-1 text-xs font-medium text-workspace-text-secondary">
                    <DollarSign className="w-3 h-3 text-green-600" />
                    Wholesale ($)
                  </label>
                  <input
                    id="default_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_cost}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        default_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                    data-testid="product-cost-input"
                    className="w-full h-9 px-3 bg-workspace-bg rounded-md border border-workspace-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  />
                </div>
              )}
              <div className={`space-y-1 ${!isAdmin ? 'col-span-2' : ''}`}>
                <label htmlFor="retail_price" className="flex items-center gap-1 text-xs font-medium text-workspace-text-secondary">
                  <ShoppingBag className="w-3 h-3 text-orange-500" />
                  Retail ($)
                </label>
                <input
                  id="retail_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.retail_price}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      retail_price: parseFloat(e.target.value) || 0,
                    })
                  }
                  data-testid="product-retail-input"
                  className="w-full h-9 px-3 bg-workspace-bg rounded-md border border-workspace-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                />
              </div>
            </div>

            {/* Colour Configuration */}
            <div className="space-y-2 p-2.5 bg-workspace-bg/50 rounded-md border border-workspace-border">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-workspace-text-secondary flex items-center gap-1">
                  <Droplet className="w-3.5 h-3.5 text-cyan-500" />
                  Colours
                </label>
                <div className="flex items-center gap-1 text-[10px]">
                  <button
                    type="button"
                    onClick={() => setColourInputMode("library")}
                    className={`px-1.5 py-0.5 rounded ${colourInputMode === "library" ? "bg-orange-500 text-white" : "bg-gray-100 hover:bg-gray-200"}`}
                  >
                    Library
                  </button>
                  <button
                    type="button"
                    onClick={() => setColourInputMode("manual")}
                    className={`px-1.5 py-0.5 rounded ${colourInputMode === "manual" ? "bg-orange-500 text-white" : "bg-gray-100 hover:bg-gray-200"}`}
                  >
                    Manual
                  </button>
                </div>
              </div>

              {colourInputMode === "library" ? (
                <div className="space-y-1.5">
                  {!formData.supplier ? (
                    <p className="text-[10px] text-amber-600 bg-amber-50 p-1.5 rounded">
                      Select a supplier first to see available colours
                    </p>
                  ) : supplierColours.length === 0 ? (
                    <div className="text-[10px] text-workspace-text-muted bg-gray-50 p-1.5 rounded">
                      <p>No colours defined for this supplier.</p>
                      <Link
                        to={`/supplier-colours${formData.supplier ? `?supplier=${encodeURIComponent(formData.supplier)}` : ""}`}
                        onClick={stashProductDraft}
                        className="text-orange-500 hover:underline"
                      >
                        Add colours →
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1.5 bg-white rounded border border-workspace-border">
                        {supplierColours.map((colour) => {
                          const isSelected = selectedColourIds.includes(colour.id);
                          return (
                            <button
                              key={colour.id}
                              type="button"
                              onClick={() => toggleColourSelection(colour.id)}
                              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-all ${
                                isSelected 
                                  ? "bg-orange-50 border-orange-500 text-orange-600" 
                                  : "bg-white border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <ColourSwatch hex={colour.hex} size="xs" />
                              {colour.name}
                              {isSelected && <Check className="w-2.5 h-2.5" />}
                            </button>
                          );
                        })}
                      </div>
                      {selectedColourIds.length > 0 && (
                        <p className="text-[10px] text-workspace-text-muted">
                          {selectedColourIds.length} selected
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  <textarea
                    id="colours"
                    value={coloursInput}
                    onChange={(e) => setColoursInput(e.target.value)}
                    placeholder="Black, White, Navy (comma separated)"
                    rows={2}
                    data-testid="product-colours-input"
                    className="w-full px-2.5 py-1.5 bg-white rounded-md border border-workspace-border text-xs resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  />
                </div>
              )}
            </div>

            {/* Size Configuration */}
            <div className="space-y-2 p-2.5 bg-workspace-bg/50 rounded-md border border-workspace-border">
              <label className="text-xs font-medium text-workspace-text-secondary">Size Config</label>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-0.5">
                  <label className="text-[10px] text-workspace-text-muted">Size System</label>
                  <Select
                    value={formData.size_type}
                    onValueChange={(value) => {
                      setFormData({ ...formData, size_type: value });
                      // Auto-populate sizes based on selected type
                      if (SIZE_PRESETS[value]) {
                        setSizesInput(SIZE_PRESETS[value].join(", "));
                      }
                    }}
                  >
                    <SelectTrigger data-testid="product-size-type-select" className="h-8 text-xs bg-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SIZE_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-0.5">
                  <label className="text-[10px] text-workspace-text-muted">Product Type</label>
                  <input
                    list="product-type-options"
                    value={formData.product_type || ""}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    placeholder="Select or type a new type..."
                    data-testid="product-type-input"
                    className="h-8 w-full px-2.5 text-xs bg-white rounded-md border border-workspace-border focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  />
                  <datalist id="product-type-options">
                    {allProductTypes.map((type) => (
                      <option key={type} value={type} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="space-y-0.5">
                <label className="text-[10px] text-workspace-text-muted">Available Sizes</label>
                <textarea
                  value={sizesInput}
                  onChange={(e) => setSizesInput(e.target.value)}
                  placeholder="XS, S, M, L, XL (comma separated)"
                  rows={1}
                  data-testid="product-sizes-input"
                  className="w-full px-2.5 py-1.5 bg-white rounded-md border border-workspace-border text-xs resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="notes" className="text-xs font-medium text-workspace-text-secondary">Notes</label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Additional notes..."
                rows={2}
                data-testid="product-notes-input"
                className="w-full px-2.5 py-1.5 bg-workspace-bg rounded-md border border-workspace-border text-xs resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/30"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, active: checked })
                }
              />
              <label htmlFor="active" className="text-xs text-workspace-text-secondary">Active</label>
            </div>
          </div>

          {/* Fixed Footer */}
          <DialogFooter className="px-5 py-3 border-t border-workspace-border gap-2 shrink-0">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 rounded-md bg-white border border-workspace-border text-sm font-medium hover:bg-workspace-hover transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave} 
              disabled={isSaving}
              className="px-4 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              {isSaving ? "Saving..." : editingProduct ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Modal */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Edit Products</DialogTitle>
            <DialogDescription>
              Update {selectedProducts.length} selected products
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Field to Update</Label>
              <Select value={bulkEditField} onValueChange={(v) => {
                setBulkEditField(v);
                setBulkEditValue("");
                setBulkSizesInput("");
                // Pre-fill sizes if selecting size_type
                if (v === "size_type") {
                  setBulkEditValue("standard_sizes");
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product_type">Product Type</SelectItem>
                  <SelectItem value="size_type">Size System</SelectItem>
                  <SelectItem value="sizes">Sizes</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {bulkEditField === "product_type" && (
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allProductTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {bulkEditField === "size_type" && (
              <div className="space-y-2">
                <Label>Size System</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select size type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {bulkEditField === "sizes" && (
              <div className="space-y-2">
                <Label>Sizes (comma separated)</Label>
                <textarea
                  value={bulkSizesInput}
                  onChange={(e) => setBulkSizesInput(e.target.value)}
                  placeholder="XS, S, M, L, XL, 2XL"
                  rows={2}
                  className="w-full px-3 py-2 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm resize-none focus:outline-none focus:ring-2 focus:ring-aa-primary"
                />
                <div className="flex flex-wrap gap-1">
                  {Object.entries(SIZE_PRESETS).map(([key, sizes]) => (
                    <button
                      key={key}
                      onClick={() => setBulkSizesInput(sizes.join(", "))}
                      className="text-xs px-2 py-0.5 bg-aa-primary/10 text-aa-primary rounded hover:bg-aa-primary/20"
                    >
                      {key.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {bulkEditField === "supplier" && (
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Input
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Enter supplier name..."
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsBulkEditOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkUpdate} 
              disabled={isBulkSaving || !bulkEditField || (!bulkEditValue && !bulkSizesInput)}
            >
              {isBulkSaving ? "Updating..." : `Update ${selectedProducts.length} Products`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Size Preset Modal */}
      <Dialog open={isSizePresetOpen} onOpenChange={setIsSizePresetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Size Preset</DialogTitle>
            <DialogDescription>
              {selectedProducts.length > 0 
                ? `Apply sizes to ${selectedProducts.length} selected products`
                : "Apply sizes to all products of a type"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedProducts.length === 0 && (
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={presetProductType} onValueChange={setPresetProductType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select product type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allProductTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Size System</Label>
              <Select value={presetSizeType} onValueChange={(v) => {
                setPresetSizeType(v);
                // Auto-populate sizes
                if (SIZE_PRESETS[v]) {
                  setPresetSizes(SIZE_PRESETS[v].join(", "));
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select size type..." />
                </SelectTrigger>
                <SelectContent>
                  {SIZE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Sizes (comma separated)</Label>
              <textarea
                value={presetSizes}
                onChange={(e) => setPresetSizes(e.target.value)}
                placeholder="XS, S, M, L, XL, 2XL"
                rows={2}
                className="w-full px-3 py-2 bg-workspace-bg rounded-md border border-workspace-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/30"
              />
              <div className="flex flex-wrap gap-1">
                {Object.entries(SIZE_PRESETS).map(([key, sizes]) => (
                  <button
                    key={key}
                    onClick={() => setPresetSizes(sizes.join(", "))}
                    className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 rounded hover:bg-orange-100"
                  >
                    {key.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsSizePresetOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleApplySizePreset} 
              disabled={isBulkSaving || !presetSizes || (selectedProducts.length === 0 && !presetProductType)}
            >
              {isBulkSaving ? "Applying..." : "Apply Sizes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Format Help */}
      <Dialog>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>CSV Import Format</DialogTitle>
            <DialogDescription>
              Your CSV should have the following columns:
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p><strong>Required:</strong> brand, style_code, product_name</p>
            <p><strong>Optional:</strong> default_cost, retail_price, supplier, available_colours, product_type, size_type, sizes</p>
            <p className="text-muted-foreground">
              Colours and sizes can be comma or semicolon separated
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
